import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Tag, Filter } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
});

const AdminBrandsPage = () => {
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [formData, setFormData] = useState({ name: '', category: '' });
  const [filterCategory, setFilterCategory] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      const [brandsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/brands`),
        axios.get(`${API}/categories`)
      ]);
      setBrands(brandsRes.data.brands_list || []);
      setCategories(categoriesRes.data.categories || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBrand) {
        await axios.put(
          `${API}/admin/brands/${editingBrand.id}`,
          formData,
          getAuthHeaders()
        );
        toast.success('Brand updated successfully');
      } else {
        await axios.post(`${API}/admin/brands`, formData, getAuthHeaders());
        toast.success('Brand created successfully');
      }
      setDialogOpen(false);
      setEditingBrand(null);
      setFormData({ name: '', category: '' });
      fetchData();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error saving brand:', error);
      const detail = error?.response?.data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => d?.msg || 'Validation error').join(', ')
          : 'Failed to save brand';
      toast.error(message);
    }
  };

  const handleEdit = (brand) => {
    setEditingBrand(brand);
    setFormData({ name: brand.name, category: brand.category });
    setDialogOpen(true);
  };

  const handleDelete = async (brandId) => {
    if (!window.confirm('Are you sure you want to delete this brand?')) return;
    
    try {
      await axios.delete(`${API}/admin/brands/${brandId}`, getAuthHeaders());
      toast.success('Brand deleted successfully');
      fetchData();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error deleting brand:', error);
      toast.error('Failed to delete brand');
    }
  };

  const openAddDialog = () => {
    setEditingBrand(null);
    setFormData({ name: '', category: categories[0]?.id || '' });
    setDialogOpen(true);
  };

  const filteredBrands = filterCategory === 'all' 
    ? brands 
    : brands.filter(b => b.category === filterCategory);

  // Group brands by category for display
  const brandsByCategory = filteredBrands.reduce((acc, brand) => {
    const cat = brand.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(brand);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading brands...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-outfit font-bold text-slate-900">
            Brands Management
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Manage product brands for your store ({brands.length} total)
          </p>
        </div>
        
        <div className="flex gap-2">
          {/* Filter by Category */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-500" />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="text-sm border border-slate-200 rounded-md px-2 py-1.5"
              data-testid="filter-category"
            >
              <option value="all">All Categories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={openAddDialog}
                className="bg-[#0047AB] hover:bg-[#003380]"
                data-testid="add-brand-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Brand
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editingBrand ? 'Edit Brand' : 'Add New Brand'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="name">Brand Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Apple"
                    required
                    data-testid="brand-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="category">Category</Label>
                  <select
                    id="category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full border border-slate-200 rounded-md px-3 py-2"
                    required
                    data-testid="brand-category-select"
                  >
                    <option value="">Select a category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-[#0047AB] hover:bg-[#003380]"
                    data-testid="save-brand-btn"
                  >
                    {editingBrand ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Brands grouped by category */}
      {Object.entries(brandsByCategory).length > 0 ? (
        <div className="space-y-6">
          {Object.entries(brandsByCategory).map(([category, categoryBrands]) => {
            const categoryInfo = categories.find(c => c.id === category);
            return (
              <div key={category} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                  <h3 className="font-semibold text-slate-900 capitalize">
                    {categoryInfo?.name || category}
                  </h3>
                  <p className="text-xs text-slate-500">{categoryBrands.length} brands</p>
                </div>
                <div className="p-4">
                  <div className="flex flex-wrap gap-2">
                    {categoryBrands.map((brand) => (
                      <div
                        key={brand.id}
                        className="group flex items-center gap-2 bg-slate-100 hover:bg-slate-200 rounded-full px-3 py-1.5 transition-colors"
                      >
                        <Tag className="w-3 h-3 text-[#0047AB]" />
                        <span className="text-sm font-medium text-slate-700">{brand.name}</span>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleEdit(brand)}
                            className="p-1 hover:bg-slate-300 rounded"
                            title="Edit"
                            data-testid={`edit-brand-${brand.id}`}
                          >
                            <Edit className="w-3 h-3 text-slate-600" />
                          </button>
                          <button
                            onClick={() => handleDelete(brand.id)}
                            className="p-1 hover:bg-red-100 rounded"
                            title="Delete"
                            data-testid={`delete-brand-${brand.id}`}
                          >
                            <Trash2 className="w-3 h-3 text-red-500" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-lg border border-slate-200">
          <Tag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No brands found</p>
          <Button
            onClick={openAddDialog}
            className="mt-4 bg-[#0047AB] hover:bg-[#003380]"
          >
            Add Your First Brand
          </Button>
        </div>
      )}
    </div>
  );
};

export default AdminBrandsPage;
